import { filter, mergeMap, catchError } from 'rxjs/operators';
import { isActionOf, PayloadAction } from 'typesafe-actions';
import { of } from 'rxjs';
import { AlertSeverity, AppEpic, ErrorData, Voyage } from './types';
import { displayAlert, toggleDisplays } from '../state/uiControl/uiControl.actions';
import {
  selectVoyage,
  selectShip,
  loadPositionsSuccess,
  loadEntries
} from '../state/voyagesData/voyagesData.actions';
import { loadShips, loadShipsRequest, loadPorts } from '../state/shipLocations/shipLocations.actions';
import { loadBulletin, loadLinks, loadSpecies } from '../state/speciesData/speciesData.actions';
import { getShipItinerary, loginUser, UserDataPayload, verifyToken } from '../state/user/user.actions';
import { ServerError, DataError } from './errors';

const alertSeverity: {
  error: AlertSeverity;
  info: AlertSeverity;
  success: AlertSeverity;
  warning: AlertSeverity;
} = {
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
};

export const loadVoyagePositionsEpic: AppEpic = (action$, state$, { fetchVoyagePositions }) =>
  action$.pipe(
    filter(isActionOf(selectVoyage.request)),
    mergeMap((params) => {
      if (!state$?.value?.voyagesData?.selectedVoyageData?.data) {
        throw new DataError('anchor', 'voyage');
      }

      const { shipCode, dateEnd, dateStart, timeEnd, timeStart } = state$.value.voyagesData.selectedVoyageData.data;

      // format dates into strings with no numeric characters so they can be used in api request
      const end = (dateEnd + timeEnd).replace(/[^0-9]/g, '');
      const start = (dateStart + timeStart).replace(/[^0-9]/g, '');

      return fetchVoyagePositions(params.payload, shipCode, start, end).pipe(
        mergeMap(response => {
          const [anchorData, locationData] = response;
          if(!response || ( !locationData[0] && (!anchorData[0] || !anchorData[0]?.data?.positions?.length))) {
            throw new DataError('anchor, neptune', 'position');
          }
          return of(
            loadPositionsSuccess({anchorData, locationData}),
            loadEntries(params.payload)
          );
        }),
        catchError(e => {
          const errorActions: [PayloadAction<any, any>] = [selectVoyage.failure(e.message)];
          if(e instanceof ServerError) {
            errorActions.push(displayAlert({message: e.message, severity: alertSeverity.error}));
          } else if(e instanceof DataError) {
            errorActions.push(displayAlert({message: e.message, severity: alertSeverity.warning}));
          }
          return of(
            ...errorActions
          );
        })
      );
    })
  );

export const loadVoyageEntriesEpic: AppEpic = (action$, state$, { fetchVoyageEntries }) =>
  action$.pipe(
    filter(isActionOf(loadEntries)),
    mergeMap((action) =>
      fetchVoyageEntries(action.payload).pipe(
        mergeMap(response => {
          return of(selectVoyage.success((response)));
        }),
        catchError(e => of(selectVoyage.failure(e.message)))
      )
    )
  );

// when a voyage is selected open the first blog entry
export const toggleFirstEntryEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(selectVoyage.success)),
    filter(() => !!state$?.value?.voyagesData?.selectedVoyageEntries?.length),
    mergeMap(() => of(toggleDisplays(state$.value.voyagesData.selectedVoyageEntries[0]))
    )
  );

export const loadShipVoyagesEpic: AppEpic = (action$, state$, { fetchVoyages }) =>
  action$.pipe(
    filter(isActionOf(selectShip.request)),
    mergeMap((params) =>
      fetchVoyages(params.payload).pipe(
        mergeMap(response => {
          if (!response || !response[0]) {
            throw new DataError('Anchor', "Voyage");
          }
          const voyages = response.filter((voyage: Voyage) => voyage.data && new Date(voyage.data.dateStart) <= new Date());

          return of(selectShip.success(voyages));
        }),
        catchError(e => {
          const errorActions: [PayloadAction<any, any>] = [selectShip.failure(e.message)];
          if(e instanceof ServerError) {
            errorActions.push(displayAlert({message: e.message, severity: alertSeverity.error}));
          } else if(e instanceof DataError) {
            errorActions.push(displayAlert({message: e.message, severity: alertSeverity.warning}));
          }
          return of(
            ...errorActions
          );
        })
      )
    )
  );

export const loadShipLocationsEpic: AppEpic = (action$, state$, { fetchShipLocations }) =>
  action$.pipe(
    filter(isActionOf(loadShipsRequest)),
    mergeMap(() =>
      fetchShipLocations().pipe(
        mergeMap(response => {
          if (!response || !response[0]) {
            return of(loadShips.failure("No result in response"));
          }
          return of(loadShips.success(response));
        }),
        catchError(e => of(loadShips.failure(e.message)))
      )
    )
  );

export const loadPortsEpic: AppEpic = (action$, state$, { getPorts }) =>
  action$.pipe(
    filter(isActionOf(loadPorts.request)),
    mergeMap(() =>
      getPorts("Alaska").pipe(
        mergeMap((alaskaResponse: any) => 
          getPorts("Antarctica").pipe(
            mergeMap((AntarcticaResponse: any) => {
              const response = alaskaResponse.concat(AntarcticaResponse);
              if (!response || !response[0]) {
                return of(loadPorts.failure("No result in response"));
              }
              return of(loadPorts.success(response));
            }),
            catchError(e => of(loadPorts.failure(e.message)))
          )
        ),
        catchError(e => of(loadPorts.failure(e.message)))
      )
    )
  );


export const loadSpeciesEpic: AppEpic = (action$, state$, { getSpecies }) =>
  action$.pipe(
    filter(isActionOf(selectVoyage.request)),
    mergeMap(() =>
      getSpecies().pipe(
        mergeMap(response => {
          if(!response || !response[0] || !response[0].data ){ 
            return of(loadSpecies.failure("Bad Species Data Recieved"));
          }
          return of(loadSpecies.success(response));
        }),
        catchError(e => of(loadSpecies.failure(e.message)))
      )
    )
  );

export const loadBulletinEpic: AppEpic = (action$, state$, { getVoyageBulletin }) =>
  action$.pipe(
    filter(isActionOf(selectVoyage.request)),
    mergeMap((action) =>
      getVoyageBulletin(action.payload).pipe(
        mergeMap(response => {
          if(!response || !response[0] || !response[0].data ){ 
            return of(loadBulletin.failure("Bad Bulletin Data Recieved"));
          }
          return of(loadBulletin.success(response));
        }),
        catchError(e => of(loadBulletin.failure(e.message)))
      )
    )
  );

export const loadWeblinksEpic: AppEpic = (action$, state$, { getVoyageWeblinks }) =>
  action$.pipe(
    filter(isActionOf(selectVoyage.request)),
    mergeMap((action) =>
      getVoyageWeblinks(action.payload).pipe(
        mergeMap(response => {
          if(!response || !response[0] || !response[0].data ){ 
            return of(loadLinks.failure("Bad Link Data Recieved"));
          }
          return of(loadLinks.success(response));
        }),
        catchError(e => of(loadLinks.failure(e.message)))
      )
    )
  );

export const loadShipItineraryEpic: AppEpic = (action$, state$, { fetchShipItinerary }) =>
  action$.pipe(
    filter(isActionOf(getShipItinerary.request)),
    mergeMap((action) =>{
      const booking = action.payload[0];
      if(!booking){

        return of(getShipItinerary.failure(
          {
            secondaryStatusCode: "",
            message: "No bookings",
            mdcKey: "",
            optionalData: {
              message: "No bookings",
            }
          }
        ));
      }
      return fetchShipItinerary(booking.sailDate,booking.disembarkationDate, booking.shipCode!).pipe(
        mergeMap(response => {
          if(response.message){
            return of(getShipItinerary.failure(response));
          }else{
            return of(getShipItinerary.success(response));
          }
        }),
        catchError(e => of(getShipItinerary.failure(e.message)))
      );
    })
  );

export const loginUserEpic: AppEpic = (action$, state$, { getLoginData }) =>
  action$.pipe(
    filter(isActionOf(loginUser.request)),
    mergeMap((action) =>
      getLoginData(action.payload).pipe(
        mergeMap((response: UserDataPayload | ErrorData) => {
          if(response.message){
            return of(loginUser.failure(response as ErrorData));
          }else{
            const responseDetails = (response as UserDataPayload).details;
            return of(loginUser.success(response as UserDataPayload), getShipItinerary.request(responseDetails.bookings));
          }
        }),
        catchError(e => of(loginUser.failure(e.message)))
      )
    )
  );
  
export const verifyTokenEpic: AppEpic = (action$, state$, { verifyUserToken }) =>
  action$.pipe(
    filter(isActionOf(verifyToken.request)),
    mergeMap((action) =>{
      const userDataString = localStorage.getItem('user');
      let userData: UserDataPayload;
      if(userDataString){
        userData = JSON.parse(userDataString);
      }
      return verifyUserToken(userData! && userData!.token ? userData!.token : "").pipe(
        mergeMap((response: ErrorData) => {
          if(response.message){
            localStorage.removeItem('user');
            return of(verifyToken.failure(response as ErrorData));
          }else{
            const responseDetails = userData.details;
            return of(loginUser.success(userData), getShipItinerary.request(responseDetails.bookings));
          }
        }),
        catchError(e => of(verifyToken.failure(e.message)))
      );
    })
  );
  